import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import GridTable from "../../components/GridTable";
import { errorMessage } from "../../components/Toaster";
import { getErrorMessage } from "../../utils/error";
import { getCompanyTrips, getTripJourney } from "./api";

const fmtMinutes = (m) => {
  if (m == null) return "—";
  if (m >= 1440) {
    const d = Math.floor(m / 1440);
    const h = Math.floor((m % 1440) / 60);
    return h ? `${d}d ${h}h` : `${d}d`;
  }
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r}m`;
  return r ? `${h}h ${r}m` : `${h}h`;
};

const fmtDateTime = (v) =>
  v
    ? new Date(v).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
    : "—";

const toInputDate = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const defaultStart = () => {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return toInputDate(d);
};

const STATUS_STYLE = {
  IN_PROGRESS: { bg: "#dbeafe", color: "#1d4ed8", label: "In Progress" },
  COMPLETED: { bg: "#dcfce7", color: "#166534", label: "Completed" },
};

const STEP_LABEL = { CHECKPOINT: "", BAY_ENTRY: " · entry", BAY_EXIT: " · exit" };

const gapLabel = (s) => {
  if (s.minutes_from_previous == null) return null;
  if (s.scan_type === "BAY_EXIT") return `${fmtMinutes(s.minutes_from_previous)} in bay`;
  return `${fmtMinutes(s.minutes_from_previous)} transit`;
};

const cardSx = { background: "#fff", border: "1px solid #e3e8ef", borderRadius: "14px" };
const labelSx = { fontSize: 12, fontWeight: 600, color: "#64748b", mb: 0.75 };
const inputSx = { background: "#fff", "& .MuiOutlinedInput-root": { height: 44, borderRadius: "8px" } };
const monoFont = "'IBM Plex Mono', ui-monospace, monospace";

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.IN_PROGRESS;
  return (
    <Typography
      component="span"
      sx={{ fontSize: 12, fontWeight: 700, px: 1.25, py: 0.5, borderRadius: "999px", background: s.bg, color: s.color, whiteSpace: "nowrap" }}
    >
      {s.label}
    </Typography>
  );
};

const SummaryChip = ({ value, label, color = "#0f172a" }) => (
  <Box sx={{ ...cardSx, borderRadius: "10px", px: 2.25, py: 1.5, display: "flex", alignItems: "baseline", gap: 1.25 }}>
    <Typography sx={{ fontSize: 22, fontWeight: 700, color }}>{value}</Typography>
    <Typography sx={{ fontSize: 13, color: "#64748b" }}>{label}</Typography>
  </Box>
);

const InfoBox = ({ label, value, purple }) => (
  <Box sx={{ background: purple ? "#f5f3ff" : "#f6f8fb", borderRadius: "10px", px: 2, py: 1.75 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, color: purple ? "#6d28d9" : "#64748b" }}>
      {label}
    </Typography>
    <Typography sx={{ mt: 0.5, fontSize: 16, fontWeight: 700, color: purple ? "#5b21b6" : "#0f172a" }}>
      {value}
    </Typography>
  </Box>
);

const Timeline = ({ steps, isOpen }) => (
  <Box>
    {steps.map((s, i) => {
      const isLast = i === steps.length - 1;
      const gap = gapLabel(s);
      return (
        <Box key={s.tag_scan_id}>
          {gap && (
            <Box sx={{ ml: "7px", borderLeft: "2px dashed #cbd5e1", py: 1, pl: "22px" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: s.scan_type === "BAY_EXIT" ? "#6d28d9" : "#475569" }}>
                {gap}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75 }}>
            <Box
              sx={{
                width: 16, height: 16, mt: "2px", borderRadius: "50%", flexShrink: 0,
                background: isLast && isOpen ? "#2563eb" : "#16a34a",
                boxShadow: isLast && isOpen ? "0 0 0 4px rgba(37,99,235,0.2)" : "none",
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                {s.location}
                {STEP_LABEL[s.scan_type]}
                {isLast && isOpen && (
                  <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, color: "#2563eb", ml: 1 }}>
                    ● current
                  </Typography>
                )}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                {fmtDateTime(s.scanned_at)} · {s.reader_serial}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    })}
  </Box>
);

const columns = [
  {
    field: "vehicle_number",
    headerName: "Vehicle",
    flex: 1,
    minWidth: 140,
    renderCell: (p) => <span style={{ fontFamily: monoFont, fontWeight: 500 }}>{p.row.vehicle_number}</span>,
  },
  { field: "tag_id", headerName: "Tag", flex: 0.7, minWidth: 100 },
  { field: "vehicle_type", headerName: "Type", flex: 0.7, minWidth: 100 },
  { field: "started_at", headerName: "Entered", flex: 1, minWidth: 140, renderCell: (p) => fmtDateTime(p.row.started_at) },
  { field: "ended_at", headerName: "Exited", flex: 1, minWidth: 140, renderCell: (p) => fmtDateTime(p.row.ended_at) },
  {
    field: "total_minutes",
    headerName: "Total time",
    flex: 0.8,
    minWidth: 110,
    renderCell: (p) => <span style={{ fontWeight: 600 }}>{fmtMinutes(p.row.total_minutes)}</span>,
  },
  { field: "current_location", headerName: "Last location", flex: 1, minWidth: 140, renderCell: (p) => p.row.current_location || "—" },
  { field: "status", headerName: "Status", flex: 0.8, minWidth: 120, renderCell: (p) => <StatusBadge status={p.row.status} /> },
];

const PlantReport = () => {
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [startDate, setStartDate] = useState(defaultStart());
  const [endDate, setEndDate] = useState(toInputDate(new Date()));

  const [allTrips, setAllTrips] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedTrip, setSelectedTrip] = useState(null);
  const [journey, setJourney] = useState([]);
  const [loadingJourney, setLoadingJourney] = useState(false);

  useEffect(() => {
    if (!startDate || !endDate) return;
    setLoading(true);
    getCompanyTrips({
      company_id: localStorage.getItem("company_id"),
      start_date: new Date(`${startDate}T00:00:00`).toISOString(),
      end_date: new Date(`${endDate}T23:59:59`).toISOString(),
    })
      .then((res) => setAllTrips(res.data || []))
      .catch((err) => errorMessage(getErrorMessage(err) || "Failed to load trips."))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  const counts = useMemo(
    () => ({
      total: allTrips.length,
      inProgress: allTrips.filter((t) => t.status === "IN_PROGRESS").length,
      completed: allTrips.filter((t) => t.status === "COMPLETED").length,
    }),
    [allTrips]
  );

  const clearFilters = () => {
    setVehicleSearch("");
    setStartDate(defaultStart());
    setEndDate(toInputDate(new Date()));
  };

  const openTrip = (trip) => {
    setSelectedTrip(trip);
    setJourney([]);
    setLoadingJourney(true);
    getTripJourney(trip.trip_id)
      .then((res) => setJourney(res.data || []))
      .catch((err) => errorMessage(getErrorMessage(err) || "Failed to load journey."))
      .finally(() => setLoadingJourney(false));
  };

  const handleTableClick = (e) => {
    const rowEl = e.target.closest(".MuiDataGrid-row");
    if (!rowEl) return;
    const id = rowEl.getAttribute("data-id");
    const trip = allTrips.find((t) => String(t.trip_id) === id);
    if (trip) openTrip(trip);
  };

  const bayTime = journey
    .filter((s) => s.scan_type === "BAY_EXIT")
    .reduce((n, s) => n + (s.minutes_from_previous || 0), 0);

  return (
    <>
      <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#0f172a" }}>Vehicle Journey</Typography>
      <Typography sx={{ fontSize: 14, color: "#64748b", mt: 0.5, mb: 2.5 }}>
        All vehicle trips · search a vehicle number to filter · click a row to see its journey
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2.5 }}>
        <SummaryChip value={counts.total} label="trips in range" />
        <SummaryChip value={counts.inProgress} label="in progress" color="#1d4ed8" />
        <SummaryChip value={counts.completed} label="completed" color="#15803d" />
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1.5, mb: 2.5 }}>
        <Box>
          <Typography sx={labelSx}>Vehicle no</Typography>
          <TextField
            size="small"
            placeholder="Search e.g. HR39AB1234"
            value={vehicleSearch}
            onChange={(e) => setVehicleSearch(e.target.value.toUpperCase().replace(/\s+/g, ""))}
            sx={{ ...inputSx, width: 260 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#64748b" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <Box>
          <Typography sx={labelSx}>From</Typography>
          <TextField
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            sx={inputSx}
            slotProps={{ htmlInput: { max: endDate } }}
          />
        </Box>

        <Box>
          <Typography sx={labelSx}>To</Typography>
          <TextField
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            sx={inputSx}
            slotProps={{ htmlInput: { min: startDate, max: toInputDate(new Date()) } }}
          />
        </Box>

        <Button
          variant="outlined"
          onClick={clearFilters}
          sx={{ height: 44, px: 2.5, borderRadius: "8px", textTransform: "none", fontWeight: 600, color: "#334155", borderColor: "#d5dce6", background: "#fff" }}
        >
          Clear
        </Button>
      </Box>

      <Box
        onClick={handleTableClick}
        sx={{
          ...cardSx,
          overflow: "hidden",
          "& .MuiDataGrid-row": { cursor: "pointer" },
          ...(selectedTrip && {
            [`& .MuiDataGrid-row[data-id="${selectedTrip.trip_id}"]`]: { backgroundColor: "#eff6ff" },
          }),
        }}
      >
        {loading && <LinearProgress />}
        <GridTable
          rows={allTrips}
          columns={columns}
          getRowId={(row) => row.trip_id}
          searchQuery={vehicleSearch}
          pageSize={10}
          density="standard"
        />
      </Box>

      <Drawer
        anchor="right"
        open={!!selectedTrip}
        onClose={() => setSelectedTrip(null)}
        sx={{ "& .MuiDrawer-paper": { width: 520, maxWidth: "100%", p: "28px 32px", boxSizing: "border-box" } }}
      >
        {selectedTrip && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.75 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
                  <Typography sx={{ fontFamily: monoFont, fontSize: 24, fontWeight: 500 }}>
                    {selectedTrip.vehicle_number}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#64748b" }}>{selectedTrip.vehicle_type}</Typography>
                </Box>
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <StatusBadge status={selectedTrip.status} />
                  <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                    {selectedTrip.status === "IN_PROGRESS"
                      ? `Currently at ${selectedTrip.current_location || "—"}`
                      : `Trip · ${new Date(selectedTrip.started_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                aria-label="Close"
                onClick={() => setSelectedTrip(null)}
                sx={{ width: 44, height: 44, border: "1px solid #e3e8ef", borderRadius: "8px" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Grid container spacing={1.5}>
              <Grid size={6}><InfoBox label="ENTERED" value={fmtDateTime(selectedTrip.started_at)} /></Grid>
              <Grid size={6}><InfoBox label="EXITED" value={fmtDateTime(selectedTrip.ended_at)} /></Grid>
              <Grid size={6}><InfoBox label="TOTAL TIME" value={fmtMinutes(selectedTrip.total_minutes)} /></Grid>
              <Grid size={6}><InfoBox label="TIME IN BAYS" value={fmtMinutes(bayTime)} purple /></Grid>
            </Grid>

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.6, color: "#64748b", mb: 1.75 }}>
                JOURNEY
              </Typography>
              {loadingJourney ? (
                <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>Loading journey…</Typography>
              ) : journey.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No scans recorded for this trip.</Typography>
              ) : (
                <Timeline steps={journey} isOpen={selectedTrip.status === "IN_PROGRESS"} />
              )}
            </Box>
          </Box>
        )}
      </Drawer>
    </>
  );
};

export default PlantReport;